import * as THREE from 'three';
import { Entity, type EntityCtx } from '../Entity';

let fireballTex: THREE.Texture | null = null;
let smokeTex: THREE.Texture | null = null;

function fbTex(): THREE.Texture {
  if (fireballTex) return fireballTex;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 4, 64, 64, 60);
  grad.addColorStop(0, 'rgba(255,240,200,1)');
  grad.addColorStop(0.3, 'rgba(255,160,60,0.9)');
  grad.addColorStop(0.7, 'rgba(170,40,10,0.6)');
  grad.addColorStop(1, 'rgba(20,10,5,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  fireballTex = new THREE.CanvasTexture(c);
  fireballTex.colorSpace = THREE.SRGBColorSpace;
  return fireballTex;
}

function smTex(): THREE.Texture {
  if (smokeTex) return smokeTex;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 6, 64, 64, 62);
  grad.addColorStop(0, 'rgba(90,80,75,0.95)');
  grad.addColorStop(0.5, 'rgba(60,55,50,0.55)');
  grad.addColorStop(1, 'rgba(30,28,26,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  // Add a few darker blobs so puffs don't all look identical
  for (let i = 0; i < 6; i++) {
    const rx = 40 + Math.random() * 48;
    const ry = 40 + Math.random() * 48;
    const rr = 8 + Math.random() * 14;
    const g2 = g.createRadialGradient(rx, ry, 1, rx, ry, rr);
    g2.addColorStop(0, 'rgba(20,18,15,0.35)');
    g2.addColorStop(1, 'rgba(20,18,15,0)');
    g.fillStyle = g2;
    g.fillRect(0, 0, 128, 128);
  }
  smokeTex = new THREE.CanvasTexture(c);
  smokeTex.colorSpace = THREE.SRGBColorSpace;
  return smokeTex;
}

let shieldTex_: THREE.Texture | null = null;
/** Electric-blue flash for shots bouncing off an invulnerable boss part — visually distinct from
 *  every real-damage explosion (always warm orange) so "my shots are doing nothing" reads at a glance. */
function shieldTex(): THREE.Texture {
  if (shieldTex_) return shieldTex_;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 4, 64, 64, 60);
  grad.addColorStop(0, 'rgba(235,250,255,1)');
  grad.addColorStop(0.3, 'rgba(120,200,255,0.95)');
  grad.addColorStop(0.7, 'rgba(40,90,200,0.55)');
  grad.addColorStop(1, 'rgba(10,20,60,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  shieldTex_ = new THREE.CanvasTexture(c);
  shieldTex_.colorSpace = THREE.SRGBColorSpace;
  return shieldTex_;
}

export type ExplosionKind = 'fire' | 'smoke' | 'shield';

export class Explosion extends Entity {
  life = 0;
  duration: number;
  sprite: THREE.Sprite;
  startScale: number;
  endScale: number;
  drift?: THREE.Vector3;

  constructor(pos: THREE.Vector3, size = 25, duration = 0.8, kind: ExplosionKind = 'fire') {
    super('fx');
    this.group.position.copy(pos);
    this.duration = duration;
    this.radius = 0.01;
    const isSmoke = kind === 'smoke';
    const mat = new THREE.SpriteMaterial({
      map: isSmoke ? smTex() : kind === 'shield' ? shieldTex() : fbTex(),
      blending: isSmoke ? THREE.NormalBlending : THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      opacity: isSmoke ? 0.85 : 1,
    });
    this.sprite = new THREE.Sprite(mat);
    // Smoke puffs start small and expand more dramatically; fire starts bigger and bursts.
    this.startScale = isSmoke ? size * 0.5 : size * 0.3;
    this.endScale = isSmoke ? size * 2.2 : size * 1.6;
    this.sprite.scale.set(this.startScale, this.startScale, 1);
    this.group.add(this.sprite);
  }

  /** Set a per-second drift vector (world units) — e.g. smoke rising, wind. */
  setDrift(v: THREE.Vector3): this { this.drift = v.clone(); return this; }

  addTo(ctx: EntityCtx) {
    ctx.game.add(this);
  }

  update(ctx: EntityCtx): void {
    this.life += ctx.dt;
    const t = Math.min(1, this.life / this.duration);
    const s = this.startScale + (this.endScale - this.startScale) * t;
    this.sprite.scale.set(s, s, 1);
    const mat = this.sprite.material as THREE.SpriteMaterial;
    // Smoke fades out smoothly; fire falls off faster at the end.
    mat.opacity = (mat.blending === THREE.NormalBlending) ? (0.85 * (1 - t)) : (1 - t);
    if (this.drift) this.group.position.addScaledVector(this.drift, ctx.dt);
    if (t >= 1) this.dead = true;
  }
}
